/**
 * Grabs a picture of the game window.
 *
 * Desktop only, on purpose: Electron can capture the real window, React UI and
 * all. A browser build could only read back the PIXI canvas, which misses the
 * entire UI layer and so isn't worth the complexity - those reports just go out
 * without one.
 *
 * The capture is taken the moment the player presses the report button, before
 * the modal opens, so it shows the screen they were looking at rather than the
 * dialog they opened on top of it. The report button itself is in the shot -
 * it's a small pill in a corner, not worth delaying the capture to hide.
 *
 * Every failure here returns null rather than throwing, and the modal offers
 * the "Capture Screenshot" choice only when it is actually holding an image -
 * so there is one rule for whether a screenshot exists, and no way to promise
 * the player one we can't deliver.
 */

/** Long enough that a wedged capture can't stop the modal from opening. */
const CaptureTimeoutMs = 1500;

function dataUrlToBytes(dataUrl: string): Uint8Array | null {
	const comma = dataUrl.indexOf(',');
	if (comma < 0) return null;
	const base64 = dataUrl.slice(comma + 1);
	try {
		const binary = atob(base64);
		const bytes = new Uint8Array(binary.length);
		for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
		return bytes;
	} catch {
		return null;
	}
}

/** True when this build can actually take a screenshot. */
function canCaptureScreenshot(): boolean {
	return typeof (window as any).electronApi?.bugReport?.captureScreenshot === 'function';
}

/**
 * Captures the window. Returns null if this build can't, or if the capture
 * fails or takes unreasonably long - a missing screenshot is never a reason to
 * hold up the report.
 */
export async function captureScreenshot(): Promise<Uint8Array | null> {
	if (!canCaptureScreenshot()) return null;

	try {
		const capture = (window as any).electronApi.bugReport.captureScreenshot();
		const timeout = new Promise<null>(resolve => setTimeout(() => resolve(null), CaptureTimeoutMs));
		const dataUrl = await Promise.race([capture, timeout]);

		if (typeof dataUrl === 'string' && dataUrl.length > 0) {
			return dataUrlToBytes(dataUrl);
		}
		if (dataUrl === null) console.warn('Bug reporter: screenshot timed out');
	} catch (e) {
		console.warn('Bug reporter: screenshot failed', e);
	}
	return null;
}
